import { Stack, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LabData, LabVariantData } from '../../AppData/LabData';
import SizeDisplay from '../../Common/Measurements/SizeDisplay';

export interface DesignGuidelinesPrintAreaSize {
  key: string
  label: string
  sizes: number[]
}

export interface DesignGuidelinesData {
  dpi?: number
  printAreaSizes: DesignGuidelinesPrintAreaSize[]
  printFileDimensions?: {
    width: number
    height: number
  }
}

interface Props {
  labData: LabData
  selectedVariant: string
  productName?: string
}

const formatScenePartSizeKey = (sceneSlug: string, partSlug: string) => {
  return sceneSlug + ':' + partSlug;
}

const getLabVariantData = (labData: LabData, selectedVariant: string): LabVariantData | undefined => {
  const variants = labData.variants as Partial<Record<string, LabVariantData>>;

  return variants[selectedVariant]
    ?? variants.default
    ?? Object.values(variants)[0];
}

const getSceneDataName = (sceneData: { name?: string }) => {
  return sceneData.name;
}

function DesignGuidelinesSummary(props: Props) {
  const [t] = useTranslation('design-lab');
  const productLabel = props.productName ?? props.labData.name;
  const sceneNameBySlug = useMemo<Record<string, string>>(() => {
    return {
      back: t('Back'),
      backside: t('Backside'),
      band: t('Band'),
      cover: t('Cover'),
      elastic: t('Elastic'),
      firstpage: t('First page'),
      foldover: t('Foldover'),
      front: t('Front'),
      gusset: t('Gusset'),
      inside: t('Inside'),
      legs: t('Legs'),
      main: productLabel,
      outside: t('Outside'),
      pockets: t('Pockets'),
      sock: t('Sock'),
    }
  }, [productLabel, t])
  const partNameBySlug = useMemo<Record<string, string>>(() => {
    return {
      default: t('Artwork'),
      back: t('Back'),
      front: t('Front'),
      left: t('Left'),
      right: t('Right'),
    }
  }, [t])
  const guidelines = useMemo<DesignGuidelinesData>(() => {
    const variantData = getLabVariantData(props.labData, props.selectedVariant);
    const printFileDimensions = variantData?.dimensions.render;
    const printAreaSizes: DesignGuidelinesPrintAreaSize[] = props.labData.dpi > 0 && variantData !== undefined
      ? Object.entries(variantData.scenes).flatMap(([sceneSlug, sceneData]) => {
        const sceneName = getSceneDataName(sceneData);
        const sceneLabel = sceneName !== undefined && sceneName !== ''
          ? sceneNameBySlug[sceneName] ?? sceneName
          : sceneNameBySlug[sceneSlug] ?? sceneSlug;
        const sceneIsGenericMain = sceneSlug === 'main' && (sceneName === undefined || sceneName === '');
        const partEntries = Object.entries(sceneData.parts);

        return partEntries.map(([partSlug, partData]) => {
          const partName = partData.name ?? partSlug;
          const partLabel = partNameBySlug[partName] ?? partName;
          const label = partEntries.length === 1 && partSlug === 'default'
            ? sceneLabel
            : sceneIsGenericMain
              ? partLabel
              : t('{{scene}} - {{part}}', {
                scene: sceneLabel,
                part: partLabel,
              });

          return {
            key: formatScenePartSizeKey(sceneSlug, partSlug),
            label,
            sizes: [partData.render.width, partData.render.height],
          };
        });
      })
      : [];

    return {
      dpi: props.labData.dpi > 0 ? props.labData.dpi : undefined,
      printAreaSizes,
      printFileDimensions,
    };
  }, [partNameBySlug, props.labData, props.selectedVariant, sceneNameBySlug, t])
  const hasDpi = guidelines.dpi !== undefined && guidelines.dpi > 0;

  const renderPrintAreaDimensions = useCallback((printAreaSize: DesignGuidelinesPrintAreaSize) => {
    if (!hasDpi || guidelines.dpi === undefined) {
      return null;
    }

    return <Stack sx={{ alignItems: 'flex-end', textAlign: 'right' }}>
      <Typography variant="body2" fontWeight={700}>
        <SizeDisplay
          sizes={printAreaSize.sizes}
          sourceUnit="px"
          dpi={guidelines.dpi}
          displayType="physical"
        />
      </Typography>
      <Typography variant="caption" color="text.secondary">
        <SizeDisplay
          sizes={printAreaSize.sizes}
          sourceUnit="px"
          displayType="digital"
        />
      </Typography>
    </Stack>
  }, [guidelines.dpi, hasDpi])

  const renderPrintAreaSize = useCallback((printAreaSize: DesignGuidelinesPrintAreaSize) => {
    return <Stack
      key={printAreaSize.key}
      direction="row"
      justifyContent="space-between"
      spacing={2}
      sx={{
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="body2">{printAreaSize.label}</Typography>
      {renderPrintAreaDimensions(printAreaSize)}
    </Stack>
  }, [renderPrintAreaDimensions])

  return <Stack spacing={1.25}>
    <Typography variant="h4">{t('Design guidelines')}</Typography>
    <Stack spacing={1}>
      {hasDpi ? <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">{t('Resolution')}</Typography>
        <Typography variant="body2" fontWeight={700}>{t('{{dpi}} DPI', { dpi: guidelines.dpi })}</Typography>
      </Stack> : null}
      {guidelines.printAreaSizes.length === 1 && hasDpi ? <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">{t('Template size')}</Typography>
        {renderPrintAreaDimensions(guidelines.printAreaSizes[0])}
      </Stack> : null}
      {guidelines.printAreaSizes.length > 1 && hasDpi ? <Stack spacing={0.75}>
        <Typography variant="body2" color="text.secondary">{t('Template size')}</Typography>
        {guidelines.printAreaSizes.map(renderPrintAreaSize)}
      </Stack> : null}
      {guidelines.printAreaSizes.length === 0 && guidelines.printFileDimensions !== undefined ? <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">{t('Print file size')}</Typography>
        <Typography variant="body2" fontWeight={700}>
          <SizeDisplay
            sizes={[guidelines.printFileDimensions.width, guidelines.printFileDimensions.height]}
            sourceUnit="px"
            displayType="digital"
          />
        </Typography>
      </Stack> : null}
    </Stack>
  </Stack>
}

export default memo(DesignGuidelinesSummary);
